import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  createScimToken,
  createSsoConnection,
  deleteSsoConnection,
  listScimTokens,
  listSsoConnections,
  listSsoIdentities,
  revokeScimToken,
  updateSsoConnection,
  type ScimToken,
  type SsoConnection,
} from './sso-api';

/**
 * Single sign-on and provisioning (context.md §18, §22). Phase 65.
 *
 * The screen an IT administrator opens once, with their identity provider's console in the other
 * tab, and should never have to open again. So it shows them the two values they need to paste back
 * — the redirect URI and the SCIM endpoint — rather than making them derive either.
 *
 * Three refusals are visible rather than implied, because each is a question somebody will
 * otherwise ask support:
 *
 * - A connection with no allowed domain authenticates nobody, and cannot be enabled.
 * - Provisioning creates accounts, so the role those accounts start with is a required choice.
 * - A secret already stored shows as stored. An empty box means "I did not retype it".
 */

/** The roles a provider may create somebody into. Mirrors the server's list, which is the authority. */
const PROVISIONABLE_ROLES = ['VIEWER', 'SALES_REP', 'SUPPORT_AGENT', 'MANAGER'];

function ConnectionDialog({
  connection,
  onClose,
}: {
  connection: SsoConnection | null;
  onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const client = useQueryClient();

  const [label, setLabel] = useState(connection?.label ?? '');
  const [issuer, setIssuer] = useState(connection?.issuer ?? '');
  const [clientId, setClientId] = useState(connection?.client_id ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [domains, setDomains] = useState((connection?.domains ?? []).join(', '));
  const [autoProvision, setAutoProvision] = useState(connection?.auto_provision ?? false);
  const [roleKey, setRoleKey] = useState(connection?.default_role_key ?? 'SALES_REP');
  const [enabled, setEnabled] = useState(connection?.enabled ?? false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const save = useMutation({
    mutationFn: async () => {
      const input = {
        label: label.trim(),
        issuer: issuer.trim(),
        clientId: clientId.trim(),
        domains: domains
          .split(/[,\s]+/)
          .map((entry) => entry.trim())
          .filter(Boolean),
        autoProvision,
        defaultRoleKey: autoProvision ? roleKey : null,
        enabled,
      };

      if (connection) {
        return updateSsoConnection(connection.id, {
          ...input,
          // Only sent when retyped: an empty box keeps what is stored.
          ...(clientSecret ? { clientSecret } : {}),
        });
      }

      return createSsoConnection({ ...input, clientSecret });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sso-connections'] });
      onClose();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const secretMissing = !connection && clientSecret.trim() === '';
  const incomplete = label.trim() === '' || issuer.trim() === '' || clientId.trim() === '';

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide"
      aria-label={connection ? t('sso.edit') : t('sso.connect')}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();

          if (!incomplete && !secretMissing) save.mutate();
        }}
      >
        <h2>{connection ? t('sso.edit') : t('sso.connect')}</h2>

        <label className="stack__field">
          <span>
            {t('sso.label')} <span className="required">*</span>
          </span>
          <input
            value={label}
            maxLength={120}
            autoFocus
            placeholder={t('sso.labelPlaceholder')}
            onChange={(event) => setLabel(event.target.value)}
          />
          <span className="muted small">{t('sso.labelHint')}</span>
        </label>

        <label className="stack__field">
          <span>
            {t('sso.issuer')} <span className="required">*</span>
          </span>
          <input
            value={issuer}
            maxLength={500}
            placeholder="https://login.microsoftonline.com/.../v2.0"
            onChange={(event) => setIssuer(event.target.value)}
          />
          <span className="muted small">{t('sso.issuerHint')}</span>
        </label>

        <label className="stack__field">
          <span>
            {t('sso.clientId')} <span className="required">*</span>
          </span>
          <input
            value={clientId}
            maxLength={255}
            onChange={(event) => setClientId(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>
            {t('sso.clientSecret')}
            {!connection && <span className="required"> *</span>}
          </span>
          <input
            type="password"
            value={clientSecret}
            autoComplete="new-password"
            placeholder={connection ? t('sso.secretKept') : ''}
            onChange={(event) => setClientSecret(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('sso.domains')}</span>
          <input
            value={domains}
            placeholder="acme.com.br, acme.com"
            onChange={(event) => setDomains(event.target.value)}
          />
          <span className="muted small">{t('sso.domainsHint')}</span>
        </label>

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={autoProvision}
            onChange={(event) => setAutoProvision(event.target.checked)}
          />
          <span>{t('sso.autoProvision')}</span>
        </label>

        {autoProvision && (
          <label className="stack__field">
            <span>{t('sso.defaultRole')}</span>
            <select
              className="select"
              value={roleKey}
              onChange={(event) => setRoleKey(event.target.value)}
            >
              {PROVISIONABLE_ROLES.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
            <span className="muted small">{t('sso.autoProvisionHint')}</span>
          </label>
        )}

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span>{t('sso.enabled')}</span>
        </label>

        {connection && (
          <p className="notice">
            <strong>{t('sso.redirectUri')}</strong>
            <br />
            <code>{connection.redirect_uri}</code>
          </p>
        )}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={incomplete || secretMissing || save.isPending}
          >
            {save.isPending ? t('work.creating') : t('sso.save')}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * Issuing a provisioning token.
 *
 * The token is in the response and nowhere else, so the dialog changes into the one place it will
 * ever be shown and says so before it is closed.
 */
function TokenDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const client = useQueryClient();

  const [label, setLabel] = useState('');
  const [roleKey, setRoleKey] = useState('SALES_REP');
  const [issued, setIssued] = useState<(ScimToken & { token: string }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const create = useMutation({
    mutationFn: () => createScimToken({ label: label.trim(), defaultRoleKey: roleKey }),
    onSuccess: (token) => {
      setIssued(token);
      void client.invalidateQueries({ queryKey: ['scim-tokens'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide"
      aria-label={t('scim.newToken')}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      {issued ? (
        <div>
          <h2>{t('scim.tokenIssued')}</h2>
          <p className="muted small">{t('scim.tokenOnce')}</p>

          <p className="stack__field">
            <span>{t('scim.endpoint')}</span>
            <code className="code-block">{issued.base_url}</code>
          </p>

          <p className="stack__field">
            <span>{t('scim.token')}</span>
            <code className="code-block">{issued.token}</code>
          </p>

          <div className="confirm__actions">
            <button type="button" className="button button--primary button--compact" onClick={onClose}>
              {t('scim.copied')}
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();

            if (label.trim()) create.mutate();
          }}
        >
          <h2>{t('scim.newToken')}</h2>

          <label className="stack__field">
            <span>
              {t('scim.tokenLabel')} <span className="required">*</span>
            </span>
            <input
              value={label}
              maxLength={120}
              autoFocus
              placeholder={t('scim.tokenLabelPlaceholder')}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>

          <label className="stack__field">
            <span>{t('scim.defaultRole')}</span>
            <select
              className="select"
              value={roleKey}
              onChange={(event) => setRoleKey(event.target.value)}
            >
              {PROVISIONABLE_ROLES.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
            <span className="muted small">{t('scim.defaultRoleHint')}</span>
          </label>

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}

          <div className="confirm__actions">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={onClose}
            >
              {t('confirm.cancel')}
            </button>

            <button
              type="submit"
              className="button button--primary button--compact"
              disabled={label.trim() === '' || create.isPending}
            >
              {create.isPending ? t('work.creating') : t('scim.issue')}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}

export function SsoPage(): React.JSX.Element {
  const client = useQueryClient();
  const [editing, setEditing] = useState<SsoConnection | null | undefined>(undefined);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connections = useQuery({ queryKey: ['sso-connections'], queryFn: listSsoConnections });
  const identities = useQuery({ queryKey: ['sso-identities'], queryFn: listSsoIdentities });
  const tokens = useQuery({ queryKey: ['scim-tokens'], queryFn: listScimTokens });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSsoConnection(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['sso-connections'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeScimToken(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['scim-tokens'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const connectionColumns: Column<SsoConnection>[] = [
    {
      key: 'label',
      header: t('sso.label'),
      render: (connection) => (
        <span className="cell-stack">
          <span className="strong">{connection.label}</span>
          <span className="muted small">{connection.issuer}</span>
        </span>
      ),
    },
    {
      key: 'domains',
      header: t('sso.domains'),
      render: (connection) =>
        connection.domains.length === 0 ? (
          <Badge tone="warning">{t('sso.noDomains')}</Badge>
        ) : (
          <span className="muted small">{connection.domains.join(', ')}</span>
        ),
    },
    {
      key: 'provision',
      header: t('sso.autoProvisionShort'),
      secondary: true,
      render: (connection) =>
        connection.auto_provision ? (
          <Badge tone="info">{connection.default_role_key ?? '—'}</Badge>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      header: t('channels.status'),
      render: (connection) => (
        <Badge tone={connection.enabled ? 'positive' : 'neutral'}>
          {t(connection.enabled ? 'sso.on' : 'sso.off')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (connection) => (
        <span className="cell-actions">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setEditing(connection)}
          >
            {t('sso.edit')}
          </button>
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={remove.isPending}
            onClick={() => remove.mutate(connection.id)}
          >
            {t('sso.remove')}
          </button>
        </span>
      ),
    },
  ];

  const tokenColumns: Column<ScimToken>[] = [
    {
      key: 'label',
      header: t('scim.tokenLabel'),
      render: (token) => (
        <span className="cell-stack">
          <span className="strong">{token.label}</span>
          <span className="muted small">
            <code>{token.token_hint}…</code>
          </span>
        </span>
      ),
    },
    {
      key: 'role',
      header: t('scim.defaultRole'),
      secondary: true,
      render: (token) => <code>{token.default_role_key}</code>,
    },
    {
      key: 'used',
      header: t('scim.lastUsed'),
      secondary: true,
      render: (token) =>
        token.last_used_at ? formatDateTime(token.last_used_at) : t('scim.neverUsed'),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (token) => (
        <button
          type="button"
          className="button button--quiet button--compact"
          disabled={revoke.isPending}
          onClick={() => revoke.mutate(token.id)}
        >
          {t('scim.revoke')}
        </button>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.sso')}
        description={t('sso.description')}
        actions={
          <button type="button" className="button button--primary" onClick={() => setEditing(null)}>
            + {t('sso.connect')}
          </button>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel title={t('sso.connections')}>
        <DataTable
          tableKey="sso-connections"
          rows={connections.data?.connections}
          columns={connectionColumns}
          rowKey={(connection) => connection.id}
          isLoading={connections.isLoading}
          error={connections.error}
          onRetry={() => void connections.refetch()}
          empty={{ title: t('sso.emptyTitle'), description: t('sso.emptyBody') }}
        />
      </Panel>

      <Panel
        title={t('scim.title')}
        description={t('scim.description')}
        actions={
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setIssuing(true)}
          >
            + {t('scim.issue')}
          </button>
        }
      >
        {tokens.data?.base_url && (
          <p className="notice">
            <strong>{t('scim.endpoint')}</strong> <code>{tokens.data.base_url}</code>
          </p>
        )}

        <DataTable
          tableKey="scim-tokens"
          rows={tokens.data?.tokens}
          columns={tokenColumns}
          rowKey={(token) => token.id}
          isLoading={tokens.isLoading}
          error={tokens.error}
          onRetry={() => void tokens.refetch()}
          empty={{ title: t('scim.emptyTitle'), description: t('scim.emptyBody') }}
        />
      </Panel>

      <Panel title={t('sso.identities')} description={t('sso.identitiesDescription')}>
        <DataTable
          tableKey="sso-identities"
          rows={identities.data?.identities}
          columns={[
            {
              key: 'person',
              header: t('team.person'),
              render: (identity) => (
                <span className="cell-stack">
                  <span className="strong">{identity.user_name}</span>
                  <span className="muted small">{identity.user_email}</span>
                </span>
              ),
            },
            {
              key: 'subject',
              header: t('sso.subject'),
              secondary: true,
              render: (identity) => <code>{identity.subject}</code>,
            },
            {
              key: 'last',
              header: t('sso.lastLogin'),
              secondary: true,
              align: 'end',
              render: (identity) =>
                identity.last_login_at ? formatDateTime(identity.last_login_at) : '—',
            },
          ]}
          rowKey={(identity) => identity.id}
          isLoading={identities.isLoading}
          error={identities.error}
          onRetry={() => void identities.refetch()}
          empty={{ title: t('sso.noIdentities') }}
        />
      </Panel>

      {editing !== undefined && (
        <ConnectionDialog connection={editing} onClose={() => setEditing(undefined)} />
      )}

      {issuing && <TokenDialog onClose={() => setIssuing(false)} />}
    </div>
  );
}
